import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Segmented,
  Typography,
  Popconfirm,
  Tooltip,
  Modal,
  Descriptions,
  Alert as AntAlert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ReloadOutlined,
  CheckOutlined,
  DeleteOutlined,
  RedoOutlined,
  HomeOutlined,
  CalendarOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  FormatPainterOutlined,
} from "@ant-design/icons";
import {
  alertsApi,
  Alerta,
  AlertaNivel,
  AlertaEstado,
  AlertaStaysMeta,
} from "../../services/alerts.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import PageLayout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { useWebSocket } from "../../context/WebSocketContext";
import { getMsg } from "../../utils/antdStatic";
import { paginacionCliente } from "../../utils/tablaPaginacion";
import { etiquetaCategoria } from "./categoriasAlerta";

const { Text, Paragraph } = Typography;

const NIVEL_COLOR: Record<AlertaNivel, string> = {
  critica: "red",
  advertencia: "orange",
  info: "blue",
};

type Filtro = "activa" | "resuelta" | "todas";

/** Lista blanca del filtro recordado: un valor viejo no puede dejar la página
 * pidiendo un estado que el backend no conoce. */
const FILTROS: Filtro[] = ["activa", "resuelta", "todas"];

const fmtFecha = (s: string | null) =>
  s ? new Date(s).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—";

/** `2026-08-15` → `15-08-2026`. Solo presentación. */
const fmtDia = (s?: string | null) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [a, m, d] = s.split("-");
  return `${d}-${m}-${a}`;
};

// El nombre en castellano de cada categoría vive en ./categoriasAlerta.ts (se prueba aparte).

const ENTIDAD_LABEL: Record<string, string> = {
  arriendo: "Arriendo",
  bloqueo: "Bloqueo",
  mantencion: "Mantención",
  desconocido: "Sin determinar",
};

const esAlertaStays = (a: Alerta) => a.categoria === "stays_outbound_failed";

/**
 * Deep-links al registro que falló. Los params NO son opcionales: el efecto de
 * /calendario exige `from` Y `to` antes de mirar nada más, así que un link
 * incompleto aterriza en el mes actual sin avisar que el salto falló. Por eso
 * devolvemos `null` cuando falta algo, y el botón simplemente no se muestra.
 */
function linkCalendario(m: AlertaStaysMeta): string | null {
  if (!m.desde || !m.hasta || m.propertyId == null) return null;
  const p = new URLSearchParams({
    from: m.desde,
    to: m.hasta,
    propertyId: String(m.propertyId),
  });
  // Solo el arriendo se puede abrir directo; un bloqueo no tiene deep-link
  // propio en todo el sistema, así que el salto es al mes + propiedad.
  if (m.entidad === "arriendo" && m.recordId != null) {
    p.set("editBooking", String(m.recordId));
  }
  return `/calendario?${p.toString()}`;
}

function linkArriendos(m: AlertaStaysMeta): string | null {
  if (m.entidad !== "arriendo" || m.recordId == null || !m.desde || !m.hasta) return null;
  // `from`/`to` son obligatorios: sin ellos la tabla filtra por mes contable y
  // un arriendo de hace tres meses no aparece — la tabla se ve vacía y el
  // banner dice «mostrando un solo arriendo». Fallo silencioso y contradictorio.
  return `/arriendos?bookingId=${m.recordId}&from=${m.desde}&to=${m.hasta}`;
}

/** El reintento solo es seguro cuando sabemos qué registro es y no fue un borrado. */
function puedeReintentar(m: AlertaStaysMeta): boolean {
  if (m.action === "delete") return false;
  if (m.recordId == null) return false;
  return m.entidad === "arriendo" || m.entidad === "bloqueo" || m.entidad === "mantencion";
}

/**
 * Ficha desplegable de una alerta. Existe porque la metadata (dónde vive lo
 * que de verdad identifica al registro y el motivo técnico del fallo) no se
 * mostraba en ninguna parte: quedaba en la base, invisible para el admin.
 */
const DetalleAlerta: React.FC<{ alerta: Alerta }> = ({ alerta }) => {
  const m = (alerta.metadata ?? {}) as AlertaStaysMeta;
  const stays = esAlertaStays(alerta);
  const desde = fmtDia(m.desde);
  const hasta = fmtDia(m.hasta);

  const items: { label: string; valor: React.ReactNode }[] = [];

  if (stays) {
    items.push({
      label: "Tipo de registro",
      valor:
        m.entidad && m.entidad !== "desconocido" ? (
          <>
            <Tag color="purple">{ENTIDAD_LABEL[m.entidad] ?? m.entidad}</Tag>
            {m.recordId != null && (
              <Text code>
                {m.entidad === "arriendo" ? `Estadía #${m.recordId}` : `id ${m.recordId}`}
              </Text>
            )}
          </>
        ) : (
          <Text type="warning">
            No se pudo determinar (alerta antigua — usa «Reprocesar alertas antiguas»)
          </Text>
        ),
    });
    items.push({
      label: "Propiedad",
      valor: m.propiedadNombre ? (
        formatPropertyName(m.propiedadNombre)
      ) : m.propertyId != null ? (
        <Text type="secondary">sin nombre (id {m.propertyId})</Text>
      ) : (
        <Text type="secondary">—</Text>
      ),
    });
    if (desde || hasta) {
      items.push({ label: "Fechas", valor: `${desde ?? "?"} → ${hasta ?? "?"}` });
    }
    if (m.huesped) items.push({ label: "Huésped", valor: m.huesped });
    if (m.refPlataforma) {
      items.push({ label: "Ref. plataforma", valor: <Text code>{m.refPlataforma}</Text> });
    }
    if (m.razon) items.push({ label: "Motivo del bloqueo", valor: m.razon });
    if (m.action) {
      items.push({
        label: "Operación que falló",
        valor:
          m.action === "create"
            ? "Crear en Stays"
            : m.action === "update"
              ? "Actualizar en Stays"
              : "Eliminar en Stays",
      });
    }
  }

  if (m.error) {
    items.push({
      label: "Motivo técnico",
      valor: <Text code>{m.error}</Text>,
    });
  }

  return (
    <div style={{ padding: "4px 8px 8px" }}>
      {items.length > 0 && (
        <Descriptions size="small" column={2} bordered>
          {items.map((it) => (
            <Descriptions.Item key={it.label} label={it.label}>
              {it.valor}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {alerta.mensaje && (
        <Paragraph style={{ whiteSpace: "pre-line", margin: "12px 0 0", fontSize: 13 }}>
          {alerta.mensaje}
        </Paragraph>
      )}

      {m.original?.titulo && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "#888" }}>
            Texto original de la alerta (antes de reprocesarla)
          </summary>
          <Paragraph
            type="secondary"
            style={{ whiteSpace: "pre-line", fontSize: 12, marginTop: 6 }}
          >
            {m.original.titulo}
            {m.original.mensaje ? `\n${m.original.mensaje}` : ""}
          </Paragraph>
        </details>
      )}

      {items.length === 0 && !alerta.mensaje && (
        <Text type="secondary">Esta alerta no guardó detalle adicional.</Text>
      )}
    </div>
  );
};

const AlertasPage: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { socket } = useWebSocket();
  // El estado elegido se recuerda por usuario admin en este navegador
  // (round 2026-09-24, ver docs/filtros-persistentes.md). El Segmented lo deja
  // a la vista, así que restaurar "Resueltas" se nota sin tener que buscarlo.
  const [filtro, setFiltro] = useFiltroPersistido<Filtro>(
    "alertas.estado",
    "activa",
    { validar: (v) => FILTROS.some((f) => f === v) },
  );
  const [rows, setRows] = useState<Alerta[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [reformateando, setReformateando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await alertsApi.listar(filtro, 300);
    setRows(data);
    setLoading(false);
  }, [filtro]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: recargar cuando cambian las alertas.
  useEffect(() => {
    if (!socket) return;
    const onMessage = (e: MessageEvent) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg?.type === "ALERTS_CHANGED") load();
      } catch {
        /* ignore */
      }
    };
    socket.addEventListener("message", onMessage);
    return () => socket.removeEventListener("message", onMessage);
  }, [socket, load]);

  const resolver = async (a: Alerta) => {
    setBusyId(a.id);
    await alertsApi.resolver(a.id);
    getMsg()?.success("Alerta resuelta");
    setBusyId(null);
    load();
  };

  const reabrir = async (a: Alerta) => {
    setBusyId(a.id);
    await alertsApi.reabrir(a.id);
    setBusyId(null);
    load();
  };

  const eliminar = async (a: Alerta) => {
    setBusyId(a.id);
    await alertsApi.eliminar(a.id);
    getMsg()?.success("Alerta eliminada");
    setBusyId(null);
    load();
  };

  const reprocesar = async (a: Alerta) => {
    const listing = a.metadata?.id_propiedad_largo;
    if (!listing) {
      getMsg()?.warning("Esta alerta no tiene un listing asociado");
      return;
    }
    setBusyId(a.id);
    const res = await alertsApi.reprocesarPropiedad(String(listing));
    setBusyId(null);
    if (res) {
      getMsg()?.success(
        `Se reencolaron ${res.reset} webhook(s) de esa propiedad. El cron los reprocesa en ~1 min.`,
      );
    }
  };

  const resolverTodas = async () => {
    await alertsApi.resolverTodas();
    getMsg()?.success("Alertas activas resueltas");
    load();
  };

  /** Vuelve a empujar a Stays el registro que quedó sin sincronizar. */
  const reintentar = async (a: Alerta) => {
    setBusyId(a.id);
    const res = await alertsApi.reintentarStays(a.id);
    setBusyId(null);
    // `apiFetch` devuelve null y ya mostró el toast del error (por ejemplo el
    // 409 «no se pudo determinar si era un arriendo o un bloqueo»).
    if (!res) return;
    if (res.sincronizado && res.motivo) {
      // Cerró la alerta SIN enviar nada (por ejemplo un bloqueo que ya terminó):
      // decir «Enviado a Stays» sería falso, así que se muestra la explicación.
      getMsg()?.info({ content: res.motivo, duration: 10 });
    } else if (res.sincronizado) {
      getMsg()?.success("Enviado a Stays. La alerta se cerró sola.");
    } else if (res.motivo) {
      // El backend sabe que no hay nada que reintentar hasta completar un dato.
      // Sin este mensaje el admin veía el mismo aviso genérico que ante un
      // timeout y apretaba el botón de nuevo sin que pudiera cambiar nada.
      getMsg()?.warning({ content: res.motivo, duration: 12 });
    } else {
      getMsg()?.warning(
        "El reintento no logró sincronizar. La alerta sigue activa con el motivo actualizado.",
      );
    }
    load();
  };

  /**
   * Reprocesa las alertas viejas al formato legible. Primero corre en seco y
   * muestra qué pasaría; recién si el admin confirma, escribe. Ese paso extra
   * existe porque la reescritura toca filas históricas.
   */
  const reformatear = async () => {
    setReformateando(true);
    const previo = await alertsApi.reformatearStays({ dryRun: true });
    setReformateando(false);
    if (!previo) return;

    if (previo.reformateadas === 0) {
      getMsg()?.info(
        previo.saltadas > 0
          ? `Nada que hacer: las ${previo.saltadas} alertas de Stays ya están al día.`
          : "No hay alertas de sincronización con Stays para reprocesar.",
      );
      return;
    }

    const e = previo.porEstado ?? {};
    Modal.confirm({
      title: `Reprocesar ${previo.reformateadas} alerta(s)`,
      width: 620,
      okText: "Reprocesar",
      cancelText: "Cancelar",
      content: (
        <div>
          <Paragraph style={{ marginBottom: 12 }}>
            Se reescribirá el texto de las alertas activas de sincronización con Stays para que
            digan qué registro falló, de qué propiedad y de qué fechas. El texto original se guarda
            por si quieres consultarlo.
          </Paragraph>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Se identificaron sin dudas">{e.unico ?? 0}</Descriptions.Item>
            <Descriptions.Item label="Quedan ambiguas (se declara en el texto)">
              {e.ambiguo ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="El registro ya no existe">{e.borrado ?? 0}</Descriptions.Item>
            <Descriptions.Item label="Sin datos recuperables (borrados antiguos)">
              {e.sin_dato ?? 0}
            </Descriptions.Item>
            {previo.saltadas > 0 && (
              <Descriptions.Item label="Ya estaban al día (no se tocan)">
                {previo.saltadas}
              </Descriptions.Item>
            )}
          </Descriptions>
          {(e.sin_dato ?? 0) > 0 && (
            <AntAlert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Algunas no se pueden recuperar"
              description={
                "Las alertas antiguas de borrado nunca guardaron qué registro falló: " +
                "el emisor de esa época no enviaba el id. Se marcarán como tales en " +
                "vez de inventar un dato. Las nuevas sí lo traen."
              }
            />
          )}
        </div>
      ),
      onOk: async () => {
        const res = await alertsApi.reformatearStays();
        if (!res) return;
        getMsg()?.success(
          `${res.reformateadas} alerta(s) reprocesada(s)` +
            (res.saltadas ? ` · ${res.saltadas} ya estaban al día` : "") +
            (res.fallidas ? ` · ${res.fallidas} con error` : ""),
        );
        load();
      },
    });
  };

  const columns: ColumnsType<Alerta> = [
    {
      title: "Nivel",
      dataIndex: "nivel",
      width: 110,
      render: (n: AlertaNivel) => <Tag color={NIVEL_COLOR[n]}>{n}</Tag>,
      filters: [
        { text: "Crítica", value: "critica" },
        { text: "Advertencia", value: "advertencia" },
        { text: "Info", value: "info" },
      ],
      onFilter: (v, r) => r.nivel === v,
    },
    {
      title: "Alerta",
      dataIndex: "titulo",
      render: (_: string, a: Alerta) => (
        <div>
          <Text strong>{a.titulo}</Text> {a.contador > 1 && <Tag>{a.contador}×</Tag>}
          {a.mensaje && (
            <Paragraph
              type="secondary"
              // `pre-line` respeta los saltos de línea del mensaje: el formato
              // nuevo es una ficha de varias líneas («Qué es / Propiedad /
              // Fechas / Qué hacer») y sin esto se leería como un párrafo pegado.
              style={{ fontSize: 12, margin: "4px 0 0", whiteSpace: "pre-line" }}
              ellipsis={{ rows: 3, expandable: true, symbol: "ver más" }}
            >
              {a.mensaje}
            </Paragraph>
          )}
        </div>
      ),
    },
    {
      title: "Categoría",
      dataIndex: "categoria",
      width: 200,
      // Se muestra el nombre en castellano, con el slug crudo en el tooltip:
      // el slug sigue siendo lo que se busca en el código y en los logs.
      render: (c: string) => (
        <Tooltip title={c}>
          <Tag>{etiquetaCategoria(c)}</Tag>
        </Tooltip>
      ),
      filters: Array.from(new Set(rows.map((r) => r.categoria))).map((c) => ({
        text: etiquetaCategoria(c),
        value: c,
      })),
      onFilter: (v, r) => r.categoria === v,
    },
    {
      title: "Última vez",
      dataIndex: "updatedAt",
      width: 150,
      render: (s: string) => <Text type="secondary">{fmtFecha(s)}</Text>,
      sorter: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      width: 110,
      render: (e: AlertaEstado) =>
        e === "activa" ? <Tag color="red">activa</Tag> : <Tag>resuelta</Tag>,
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 430,
      render: (_: unknown, a: Alerta) => (
        <Space wrap>
          {a.categoria === "stays_property_not_found" && (
            <>
              <Tooltip title="Re-encolar todos los webhooks de esta propiedad (después de registrarla)">
                <Button
                  size="small"
                  icon={<RedoOutlined />}
                  loading={busyId === a.id}
                  onClick={() => reprocesar(a)}
                >
                  Reprocesar
                </Button>
              </Tooltip>
              <Tooltip title="Ir a registrar/editar la propiedad">
                <Button
                  size="small"
                  icon={<HomeOutlined />}
                  onClick={() => navigate("/propiedades")}
                >
                  Propiedades
                </Button>
              </Tooltip>
            </>
          )}
          {esAlertaStays(a) &&
            (() => {
              const m = (a.metadata ?? {}) as AlertaStaysMeta;
              const cal = linkCalendario(m);
              const arr = linkArriendos(m);
              return (
                <>
                  {arr && (
                    <Tooltip title="Ver el arriendo en la tabla, sin riesgo de editarlo">
                      <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(arr)}>
                        Ver
                      </Button>
                    </Tooltip>
                  )}
                  {cal && (
                    <Tooltip
                      title={
                        m.entidad === "arriendo"
                          ? "Abrir el arriendo en el calendario. Guardarlo sin cambios reintenta el envío a Stays."
                          : "Saltar a esas fechas y propiedad en el calendario. Un bloqueo no tiene código propio, así que se identifica por fecha y motivo."
                      }
                    >
                      <Button
                        size="small"
                        icon={<CalendarOutlined />}
                        onClick={() => navigate(cal)}
                      >
                        Calendario
                      </Button>
                    </Tooltip>
                  )}
                  {puedeReintentar(m) && (
                    <Tooltip title="Volver a empujar este registro a Stays ahora, sin abrirlo">
                      <Button
                        size="small"
                        icon={<ThunderboltOutlined />}
                        loading={busyId === a.id}
                        onClick={() => reintentar(a)}
                      >
                        Reintentar
                      </Button>
                    </Tooltip>
                  )}
                </>
              );
            })()}
          {a.estado === "activa" ? (
            <Button
              size="small"
              type="text"
              icon={<CheckOutlined />}
              loading={busyId === a.id}
              onClick={() => resolver(a)}
            >
              Resolver
            </Button>
          ) : (
            <Button size="small" type="text" loading={busyId === a.id} onClick={() => reabrir(a)}>
              Reabrir
            </Button>
          )}
          <Popconfirm
            title="¿Eliminar esta alerta?"
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => eliminar(a)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    // Sin este <Layout> la página quedaba sin barra lateral NI header: se
    // entraba a /alertas desde la campana y no había forma de volver ni de
    // navegar a otra sección. Todas las páginas montan su propio Layout.
    <PageLayout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div style={{ padding: 16 }}>
        <PageHeader
          title="Gestor de alertas"
          helpText="Problemas operativos que necesitan que alguien haga algo: reservas que llegaron de una propiedad no registrada, cambios que no se pudieron enviar a Stays, precios bajo el mínimo pactado. Se resuelven solas cuando el problema desaparece. Despliega una fila para ver el detalle completo."
          extra={
            <Space wrap>
              <Segmented
                value={filtro}
                onChange={(v) => setFiltro(v as Filtro)}
                options={[
                  { label: "Activas", value: "activa" },
                  { label: "Resueltas", value: "resuelta" },
                  { label: "Todas", value: "todas" },
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={load}>
                Refrescar
              </Button>
              <Tooltip title="Reescribe las alertas antiguas de sincronización con Stays para que digan qué registro falló, de qué propiedad y de qué fechas. Muestra un previo antes de aplicar.">
                <Button
                  icon={<FormatPainterOutlined />}
                  loading={reformateando}
                  onClick={reformatear}
                >
                  Reprocesar alertas antiguas
                </Button>
              </Tooltip>
              {filtro === "activa" && rows.length > 0 && (
                <Popconfirm
                  title="¿Resolver todas las alertas activas?"
                  okText="Resolver todas"
                  cancelText="Cancelar"
                  onConfirm={resolverTodas}
                >
                  <Button icon={<CheckOutlined />}>Resolver todas</Button>
                </Popconfirm>
              )}
            </Space>
          }
        />

        <Card styles={{ body: { padding: 0 } }}>
          <Table<Alerta>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            size="middle"
            expandable={{
              // La ficha desplegable muestra la metadata, que antes no se
              // renderizaba en ningún lado: el admin veía el texto y nada más.
              expandedRowRender: (a) => <DetalleAlerta alerta={a} />,
              rowExpandable: (a) => Boolean(a.metadata) || Boolean(a.mensaje),
            }}
            pagination={paginacionCliente({ porDefecto: 20, etiqueta: "alerta" })}
          />
        </Card>
      </div>
    </PageLayout>
  );
};

export default AlertasPage;
